// src/pages/settings/Branding.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Heading,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  Spinner,
  Text,
  useToast,
  HStack,
  Flex,
} from '@chakra-ui/react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import useOrgClaims from '../../hooks/useOrgClaims';

export default function Branding() {
  const toast = useToast();
  const { orgId } = useOrgClaims(); // resolves orgId from auth claims
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [companyName, setCompanyName] = useState('');

  // Load current branding
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!orgId) {
        setCompanyName('');
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, 'organizations', orgId));
        if (!cancelled) {
          setCompanyName(snap.exists() ? snap.data()?.companyName || '' : '');
        }
      } catch (e) {
        if (!cancelled) {
          toast({
            status: 'error',
            title: 'Failed to load branding',
            description: String(e?.message || e),
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [orgId, toast]);

  // Save branding
  const handleSave = async () => {
    if (!orgId) {
      toast({ status: 'warning', title: 'Organisation not set' });
      return;
    }
    setSaving(true);
    try {
      await setDoc(
        doc(db, 'organizations', orgId),
        { companyName: companyName || '', updatedAt: serverTimestamp() },
        { merge: true }
      );
      toast({ status: 'success', title: 'Branding saved' });
    } catch (e) {
      toast({ status: 'error', title: 'Failed to save', description: String(e?.message || e) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box borderWidth={1} borderRadius="lg" p={6} mt={0} boxShadow="lg" bg="white">
      <Heading size="lg" mb={6} color="blue.600">
        Branding
      </Heading>
      <Text fontSize="md" color="gray.600" mb={6}>
        Set your company's name to be used in all external communication. More to come soon...
      </Text>

      {!orgId ? (
        <Text fontSize="md" color="gray.600">
          An organisation is required to edit branding.
        </Text>
      ) : loading ? (
        <HStack spacing={4}>
          <Spinner size="lg" />
          <Text fontSize="lg">Loading…</Text>
        </HStack>
      ) : (
        <VStack align="stretch" spacing={6}>
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
              Company name
            </FormLabel>
            <Input
              size="lg"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="e.g. Spicy Frames Limited"
            />
          </FormControl>
          <Flex justify="flex-end" pt={4}>
            <Button colorScheme="blue" size="lg" onClick={handleSave} isLoading={saving}>
              Save
            </Button>
          </Flex>
        </VStack>
      )}
    </Box>
  );
}
